import { useCallback, useEffect, useRef, useState } from 'react';
import { permissionsAlreadyGranted, requestPermissions, type PermissionResult } from '../capture';
import { noteEvent } from '../lib/diagnostics';

/**
 * Camera and mic acquisition, lifted out of the old PermissionScreen so the
 * title page can own the ask — the storyboard puts the two permission rows on
 * the title board rather than on a screen of its own.
 *
 * Mic denial does not break anything: the guest transforms and the clip is
 * silent. Only a camera refusal stops the flow, and that still gets its own
 * screen with a real retry rather than a dead end.
 */
export type GateState = 'checking' | 'idle' | 'asking' | 'granted' | 'denied';

export interface PermissionGate {
  /** `checking` until we know whether a browser dialog is coming at all. */
  state: GateState;
  /** Null until a grant lands. */
  result: PermissionResult | null;
  ask: () => void;
}

export function usePermissionGate(): PermissionGate {
  const [state, setState] = useState<GateState>('checking');
  const [result, setResult] = useState<PermissionResult | null>(null);
  const startedRef = useRef(false);

  /**
   * Acquire and record. Shared by the auto path and the button, because a
   * granted permission still does not guarantee a camera — another app can
   * hold the device — and both paths need the same denial handling.
   */
  const acquire = useCallback(async () => {
    const acquired = await requestPermissions();
    setResult(acquired);
    setState(acquired.cameraGranted ? 'granted' : 'denied');
  }, []);

  useEffect(() => {
    // Run once: StrictMode double-invokes effects in dev and opening the camera
    // twice is a real cost. Deliberately no cancel-on-cleanup flag — StrictMode's
    // synthetic unmount fires between the two runs, so a flag would be tripped
    // by the first cleanup while the ref blocks the second run, leaving the
    // screen stuck rendering nothing.
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const granted = await permissionsAlreadyGranted();
      if (!granted) {
        setState('idle');
        return;
      }
      noteEvent('permission already granted — acquiring quietly');
      await acquire();
    })();
  }, [acquire]);

  const ask = useCallback(() => {
    setState('asking');
    void acquire();
  }, [acquire]);

  return { state, result, ask };
}
